import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/fanhao';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">你查找的文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter(
    (a) => article.relatedSlugs.includes(a.slug)
  );

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    dateModified: article.updatedDate,
    author: {
      '@type': 'Organization',
      name: '番号查询与解析完全指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '番号查询与解析完全指南',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://formatguide.github.io/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://formatguide.github.io/category/${article.category}`, name: article.categoryName } },
      { '@type': 'ListItem', position: 3, item: { '@id': `https://formatguide.github.io/article/${article.slug}`, name: article.title } },
    ],
  };

  const faqLd = article.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />
      {faqLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
        />
      )}

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6 w-full">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <article>
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    <i className="ri-time-line mr-1"></i>
                    {article.readTime} 分钟阅读
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    <i className="ri-calendar-line mr-1"></i>
                    发布于 {article.publishDate}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    更新于 {article.updatedDate}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
              </header>

              {/* Thumbnail */}
              <div className="aspect-video md:aspect-[21/9] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} — 番号查询与解析完全指南`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-base max-w-none mb-12
                  prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                  prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:md:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-li:text-foreground-700 prose-li:leading-relaxed
                  prose-strong:text-foreground-950 prose-strong:font-semibold
                  prose-table:border prose-table:border-background-200 prose-table:rounded-lg
                  prose-th:bg-background-100 prose-th:px-4 prose-th:py-2 prose-th:text-sm prose-th:font-semibold prose-th:text-foreground-900
                  prose-td:px-4 prose-td:py-2 prose-td:text-sm prose-td:text-foreground-700 prose-td:border-t prose-td:border-background-200
                "
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex items-center gap-2 flex-wrap mb-10">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Article FAQ */}
              {article.faq.length > 0 && (
                <div className="mb-10 p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                    常见问题
                  </h3>
                  <div className="space-y-4">
                    {article.faq.map((f, i) => (
                      <div key={i}>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                          {f.question}
                        </h4>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {f.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-10">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={related.thumbnail}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-foreground-400">
                          {related.categoryName}
                        </span>
                        <h4 className="font-heading text-sm font-semibold text-foreground-950 mt-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}